import { useMemo } from 'react';

import type { GalleryImage } from '../ImageGallery.common';
import { getLightboxImageBounds, getLightboxPlaceholderSize } from '../utils';

import { useImageSize, useScrollbarTrackWidth, useViewportSize } from './index';
import { ImageSize } from './useImageSize.common';

interface UseImageGalleryOptions {
  totalImageCount?: number;
  /** The height of the footer, caption, and date under the photo, which the bounds leave room for. */
  belowStageHeight?: number;
}

export interface UseImageGalleryReturn {
  currentUri: string;
  currentSize: ImageSize;
  /** The box the photo is fitted into, which the lightbox holds at a steady height. */
  bounds: ImageSize;
  /** Holds the photo's place while it loads, or when it has no size yet. */
  placeholderSize: ImageSize;
  isCurrentImageFirst: boolean;
  isCurrentImageLast: boolean;
  hasNextImage: boolean;
  hasPrevImage: boolean;
  currentIndex: number;
}

export const useImageGallery = (
  images: GalleryImage[] | undefined,
  lightboxIndex: number,
  options?: UseImageGalleryOptions
): UseImageGalleryReturn => {
  const viewportSize = useViewportSize();
  const scrollbarWidth = useScrollbarTrackWidth();
  const belowStageHeight = options?.belowStageHeight ?? 0;
  const bounds = useMemo(
    () => getLightboxImageBounds(viewportSize, { scrollbarWidth, belowStageHeight }),
    [viewportSize, scrollbarWidth, belowStageHeight]
  );
  const totalCount = options?.totalImageCount ?? images?.length ?? 0;

  const currentImage = images?.[lightboxIndex];
  const currentUri = currentImage?.largeUncropped ?? '';

  const intrinsicSize = useMemo<ImageSize>(
    () => ({
      width: currentImage?.originalWidth ?? 0,
      height: currentImage?.originalHeight ?? 0,
    }),
    [currentImage?.originalWidth, currentImage?.originalHeight]
  );

  const currentSize = useImageSize(currentUri, bounds, intrinsicSize);
  const placeholderSize = useMemo(() => getLightboxPlaceholderSize(bounds), [bounds]);

  const isCurrentImageFirst = lightboxIndex === 0;
  const isCurrentImageLast = images ? lightboxIndex === totalCount - 1 : false;
  const hasNextImage = images ? lightboxIndex < totalCount - 1 : false;
  const hasPrevImage = lightboxIndex > 0;

  return {
    currentUri,
    currentSize,
    bounds,
    placeholderSize,
    isCurrentImageFirst,
    isCurrentImageLast,
    hasNextImage,
    hasPrevImage,
    currentIndex: lightboxIndex,
  };
};
